import { useMemo } from "react";

import { useRouter } from "next/router";

import NewselaGlobalLayout from "@/base";
import RootProviders from "@/base/RootProviders";
import useContentMetadataGet from "@/content/useContentMetadataGet";
import ResponseError from "@/errors/ResponseError";
import { API_BASE_URL } from "@/api/api.config";
import evaluateRestQuery from "@/remoteState/evaluateRestQuery";
import { UseRestQueryResponse } from "@/remoteState/useRestQuery";
import { Article } from "@/types/models/Article.interface";
import {
  LangstonContent,
  LangstonContentResponse,
} from "@/types/models/Content.interface";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import authenticatedServerSideRequestConfig from "@/utils/authenticatedServerSideRequestConfig";
import contentTypes from "@/view/constants/contentTypes";
import { UNLEVELLED_CONTENT_TYPES } from "@/view/constants/contentTypes/contentTypes.constants";
import ContentHead, {
  ContentHeadProps,
  getContentHeadSEOData,
  Metadata,
} from "@/view/ContentHead";
import UnauthViewPageContent from "@/view/UnauthViewPageContent";
import UnlevelledContent from "@/view/UnlevelledContent/UnlevelledContent";
import getDefaultArticleLevel from "@/view/utils/getDefaultArticleLevel/getDefaultArticleLevel";
import useArticleStoryGet from "@/view/utils/useArticleStoryGet";
import useBannerMode from "@/view/utils/useBannerMode";
import useExternalViewPageTracking from "@/view/utils/useExternalViewPageTracking";
import useRedirectToReadPageIfContentDoesNotExist from "@/view/utils/useRedirectToReadPageIfContentDoesNotExist";
import ViewPageContent from "@/view/ViewPageContent";
import ViewPageError from "@/view/ViewPageError";
import { ViewPageContentContextProvider } from "@/view/ViewPageContent/useViewPageContentContext";
import useViewPageContentContext from "@/view/ViewPageContent/useViewPageContentContext";
import type { GetServerSidePropsResult, NextPageContext } from "next";

const ViewPage = () => {
  // Router
  const router = useRouter();
  const { query } = router;
  const { contentId, redirectUrl: readPageRedirectUrl } = query as {
    contentId: string;
    redirectUrl: string | undefined;
  };

  // User data
  const { data: user, loading: userLoading } = useUser();
  const userProperties = useMemo(
    () => getUserProperties(user, { shouldUseNewUserApi: false }),
    [user],
  );
  const { isLiteStudent, isAuthenticatedUser, isLiteUser } = userProperties;

  const { content, contentRequestError, isContentLoading } =
    useViewPageContentContext();

  const { data: articleStoriesWithHeaders } = useArticleStoryGet(contentId);

  // Fetching content metadata
  const {
    data: contentMetadata,
    error: contentMetadataRequestError,
    loading: contentMetadataLoading,
  }: UseRestQueryResponse<Metadata | object> = useContentMetadataGet(contentId);

  // Banner mode
  const bannerMode = useBannerMode(user);
  const { isBannerModeActive } = bannerMode;

  // Page hooks
  useRedirectToReadPageIfContentDoesNotExist(content, contentRequestError);
  useExternalViewPageTracking({ content });

  // Booleans and page variables
  const pageIsLoading = userLoading || isContentLoading;
  // If the content is not available on the View page, we should load the global layout
  // without contents until the redirect to the Read page is complete.
  const shouldRedirectToReadPage = readPageRedirectUrl && contentRequestError;
  // Lite Student should see an error page for premium content
  const shouldPreventStudentContentView =
    isLiteStudent && content?.data.is_premium;

  if (pageIsLoading || shouldRedirectToReadPage) {
    return <NewselaGlobalLayout mainContents={null} />;
  }

  const isVideo = content?.content_type === contentTypes.INTERACTIVE_VIDEO;

  const isUnlevelledContent =
    content && UNLEVELLED_CONTENT_TYPES.includes(content.content_type);

  if (
    contentRequestError ||
    shouldPreventStudentContentView ||
    (!isAuthenticatedUser && isVideo)
  ) {
    return (
      <NewselaGlobalLayout
        mainContents={
          <ViewPageError
            legacyArticleSlug={articleStoriesWithHeaders?.[0]?.slug}
            statusCode={
              (contentRequestError as unknown as ResponseError)?.statusCode
            }
            userProperties={userProperties}
            contentId={contentId}
          />
        }
      />
    );
  }

  if (!isAuthenticatedUser) {
    return (
      <NewselaGlobalLayout
        mainContents={
          <UnauthViewPageContent
            content={content}
            contentMetadata={contentMetadata as Metadata}
            isLevelledContent={!isUnlevelledContent && !isVideo}
          />
        }
      />
    );
  }

  /**
   * TODO:
   *  Interactive videos are technically unlevelled content, but they
   *  have not been migrated into the UnlevelledContent component yet.
   * See task LESS-4159 for more details.
   */
  if (isUnlevelledContent) {
    /**
     * TODO:
     *  Lite teachers should see a preview banner on unlevelled content (in LESS-4565)
     *  For now, we will force the "Subscription required" error page for all Lite users.
     */
    if (isLiteUser) {
      return (
        <NewselaGlobalLayout
          mainContents={
            <ViewPageError
              statusCode={403}
              userProperties={userProperties}
              contentId={contentId}
            />
          }
        />
      );
    }
    return (
      <NewselaGlobalLayout
        mainContents={
          <UnlevelledContent bannerMode={bannerMode} content={content} />
        }
      />
    );
  }

  return (
    <NewselaGlobalLayout
      hideHeader={isBannerModeActive}
      mainContents={
        <ViewPageContent
          articleStoriesWithHeaders={articleStoriesWithHeaders}
          bannerMode={bannerMode}
          contentMetadata={contentMetadata}
          contentMetadataLoading={contentMetadataLoading}
          contentMetadataRequestError={
            contentMetadataRequestError as unknown as ResponseError
          }
          user={user}
          userProperties={userProperties}
        />
      }
    />
  );
};

const ViewPageWrapper = ({
  contentSEOMetaData,
}: {
  contentSEOMetaData: ContentHeadProps;
}) => {
  return (
    <>
      <ContentHead {...contentSEOMetaData} />
      <ViewPageContentContextProvider>
        <ViewPage />
      </ViewPageContentContextProvider>
    </>
  );
};

export default ViewPageWrapper;

export async function getServerSideProps(
  ctx: NextPageContext,
): Promise<GetServerSidePropsResult<object>> {
  const RootProvidersInitialProps = await RootProviders.__getInitialProps({
    ctx,
  });
  const {
    query: { levelId = null, contentId = null },
  } = ctx;
  // Retrieve the content item
  const contentDocumentUrl = `${API_BASE_URL}/api/v3/content-document/${contentId as string}/`;
  const contentRequest = new Request(
    contentDocumentUrl,
    authenticatedServerSideRequestConfig(ctx),
  );
  const { data: { data: contentData = {} } = {} } = (await evaluateRestQuery(
    contentRequest,
  )) as { data: { data?: LangstonContentResponse } };
  let defaultArticleLevel = null;
  // If the content is an Article, determine which level should be rendered by default.
  if ((contentData as LangstonContent)?.content_type === contentTypes.ARTICLE) {
    defaultArticleLevel =
      levelId || getDefaultArticleLevel(contentData as Article);
  }
  // Retrieve metadata for the content item.
  const metaDataUrl = `${contentDocumentUrl}metadata/`;
  const contentMetadataRequest = new Request(
    metaDataUrl,
    authenticatedServerSideRequestConfig(ctx),
  );
  const { data: contentMetadata = [] } = await evaluateRestQuery(
    contentMetadataRequest,
  );
  return {
    props: {
      [RootProviders.InitialPropsName]: RootProvidersInitialProps,
      // We can't simply pass contentData, because those objects may exceed 128KB
      // which is a limit set by the Next.js framework https://nextjs.org/docs/messages/large-page-data
      // Instead, we only pass data that's needed.
      contentSEOMetaData: getContentHeadSEOData(
        contentData as LangstonContent,
        contentMetadata as Metadata[],
        defaultArticleLevel as string,
      ),
    },
  };
}
